import { useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import {
  CheckCircle,
  AlertCircle,
  FileText,
  Camera,
  MessageSquare,
  ArrowLeft,
  Send,
  Eye,
  Edit,
  Calendar,
  User,
  Building,
  Loader2
} from 'lucide-react';
import cloudinaryService from '../cloudinaryService';
import { useToast } from '../Toast';
import { useTranslation } from '../hooks/useTranslation';
import LanguageToggle from './ui/LanguageToggle';

// Utility function for class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Image Gallery Component
const ImageGallery = ({ images, title }) => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);

  if (!images || images.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        <Camera className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{t('summary.no_images')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">{title} ({images.length})</p>
      <div className="grid grid-cols-3 gap-2">
        {images.map((image, index) => (
          <div key={image.id || index} className="relative group cursor-pointer">
            <img 
              src={cloudinaryService.getThumbnailUrl(image.url, 100)} 
              alt={image.alt || `Image ${index + 1}`}
              className="w-full h-16 object-cover rounded border hover:opacity-80 transition-opacity"
              onClick={() => setSelectedImage(image)}
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
              <Eye className="h-4 w-4 text-white" />
            </div>
          </div>
        ))}
      </div>

      {/* Full Image View Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          {selectedImage && (
            <div className="relative">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.alt} 
                className="w-full h-auto max-h-[90vh] object-contain"
              />
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white"
                onClick={() => setSelectedImage(null)}
              >
                {t('summary.close')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Check Item Summary Component
const CheckItemSummary = ({ check, checkId, controlItemTitle, controlItemTitleItalian, onEdit, onToggleFixed }) => {
  const { t, language } = useTranslation();
  // Handle different field name formats
  const deductionPoints = check.deductionPoints ?? check.deduction_points ??
    check.originalCheck?.deduction_points ?? 0;
  const isGroupB = deductionPoints > 0;
  const isFixedBeforeShipment = check.isFixedBeforeShipment || false;
  
  const hasImages = (check.images?.length > 0) || 
                   (check.image_urls?.length > 0) ||
                   (check.attachments?.length > 0);
  const hasComment = (check.comment?.trim()) || 
                    (check.remark?.trim()) ||
                    (check.notes?.trim());
  
  const hasRequiredFields = isGroupB ? (hasImages && hasComment) : true;

  return (
    <div className={cn(
      "border rounded-lg p-3 space-y-2",
      check.isChecked 
        ? hasRequiredFields 
          ? "border-red-200 bg-red-50" 
          : "border-yellow-200 bg-yellow-50"
        : "border-gray-200 bg-gray-50"
    )}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Control Item hierarchy - English and Italian */}
          {controlItemTitle && (
            <div className="mb-2 text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200">
              <div className="font-medium text-blue-800">📋 {controlItemTitle}</div>
              {controlItemTitleItalian && controlItemTitleItalian !== controlItemTitle && (
                <div className="text-blue-600 italic mt-0.5">{controlItemTitleItalian}</div>
              )}
            </div>
          )}
          
          <div className="flex items-center gap-2">
            {check.isChecked ? (
              hasRequiredFields ? (
                <CheckCircle className="h-4 w-4 text-red-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-yellow-600" />
              )
            ) : (
              <div className="h-4 w-4 rounded border border-gray-300" />
            )}
            <div className="flex-1">
              <span className={cn(
                "text-sm font-medium block",
                check.isChecked ? "text-gray-900" : "text-gray-500"
              )}>
                {language === 'it' && (check.checkCodeIt || check.labelItalian) ? (check.checkCodeIt || check.labelItalian) : (check.checkCode || check.label)}
              </span>
              {/* Always show the alternate language as secondary text */}
              {(check.checkCodeIt || check.labelItalian) && (check.checkCodeIt || check.labelItalian) !== (check.checkCode || check.label) && (
                <p className="text-xs text-gray-600 italic mt-0.5">
                  {language === 'it' ? (check.checkCode || check.label) : (check.checkCodeIt || check.labelItalian)}
                </p>
              )}
            </div>
            {deductionPoints > 0 && (
              <Badge variant="destructive" className="text-xs">
                -{deductionPoints} {t('summary.points')}
              </Badge>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(check)}
          className="h-6 w-6 p-0"
        >
          <Edit className="h-3 w-3" />
        </Button>
      </div>

      {check.isChecked && (
        <div className="ml-6 space-y-2">
          {check.comment && (
            <div className="flex items-start gap-2">
              <MessageSquare className="h-3 w-3 text-blue-600 mt-0.5" />
              <p className="text-xs text-gray-700">{check.comment}</p>
            </div>
          )}

          {check.images && check.images.length > 0 && (
            <ImageGallery
              images={check.images}
              title={t('summary.attached_images')}
            />
          )}

          {isGroupB && !hasRequiredFields && (
            <div className="text-xs text-yellow-700 bg-yellow-100 p-2 rounded">
              ⚠️ {t('summary.missing_required')} {!check.images?.length ? t('summary.image') : ''} {!check.images?.length && !check.comment?.trim() ? t('summary.and') : ''} {!check.comment?.trim() ? t('summary.comment') : ''}
            </div>
          )}

          {/* Fixed Before Shipment Checkbox - Only for items with deduction points > 0 */}
          {isGroupB && (
            <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
              <input
                type="checkbox"
                id={`fixed-${checkId}`}
                checked={isFixedBeforeShipment}
                onChange={(e) => onToggleFixed && onToggleFixed(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
              />
              <label
                htmlFor={`fixed-${checkId}`}
                className="text-sm font-medium text-gray-700 cursor-pointer select-none"
              >
                Fixed Before Shipment
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Control Item Summary Component
const ControlItemSummary = ({ controlItem, controlItemId, categoryId, categoryName, category, onEdit, onToggleFixed }) => {
  const { t, language } = useTranslation();
  // Keep checks as entries to preserve checkId
  const checksEntries = Object.entries(controlItem.checks || {});
  const checks = Object.values(controlItem.checks || {});
  const checkedItems = checks.filter(check => check.isChecked);
  const totalChecks = checks.length;
  
  // Check completion status
  const isComplete = checkedItems.length > 0 && checkedItems.every(check => {
    const deductionPoints = check.deductionPoints ?? check.deduction_points ??
      check.originalCheck?.deduction_points ?? 0;

    // If no deduction points, consider complete
    if (deductionPoints === 0) return true;

    // If has deduction points, needs images and comment
    return check.images?.length > 0 && check.comment?.trim();
  });

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">
              {language === 'it' && controlItem.controlItemTranslation
                ? controlItem.controlItemTranslation
                : controlItem.controlItemName}
            </CardTitle>
            {controlItem.controlItemTranslation && controlItem.controlItemTranslation !== controlItem.controlItemName && (
              <p className="text-sm text-gray-600">
                {language === 'it' ? controlItem.controlItemName : controlItem.controlItemTranslation}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={isComplete ? "default" : "secondary"}>
              {isComplete ? t('summary.complete') : checkedItems.length > 0 ? t('summary.incomplete') : t('summary.not_started')}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(categoryId, category, controlItem, controlItemId)}
            >
              <Edit className="h-4 w-4 mr-1" />
              {t('summary.edit')}
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          {checkedItems.length} of {totalChecks} checks selected
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {checksEntries
            .filter(([checkId, check]) => check.isChecked)
            .map(([checkId, check]) => (
              <CheckItemSummary
                key={checkId}
                check={check}
                checkId={checkId}
                controlItemTitle={controlItem.controlItemName}
                controlItemTitleItalian={controlItem.controlItemTranslation}
                onEdit={(checkItem) => onEdit(categoryId, category, controlItem, controlItemId, checkItem)}
                onToggleFixed={(isFixed) => onToggleFixed && onToggleFixed(categoryId, controlItemId, checkId, isFixed)}
              />
            ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Main Audit Summary Page Component
const AuditSummaryPage = ({
  auditFormData,
  currentAuditResponse,
  cumulativeAuditData,
  currentScore,
  selectedForm,
  onNavigateBack,
  onNavigateToDashboard,
  onEditCategory,
  onEditControlItem,
  onFinalSubmit,
  isSubmitting = false,
  onUpdateCheckFixedStatus
}) => {
  const { t, language } = useTranslation();
  const { toast } = useToast();
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [activeTab, setActiveTab] = useState('problems'); // 'problems' or 'all'

  // Enrich cumulative data with names from audit form data
  const enrichedCumulativeData = useMemo(() => {
    console.log('🔍 ENRICHMENT DEBUG:');
    console.log('  auditFormData:', auditFormData);
    console.log('  auditFormData.hierarchy:', auditFormData?.hierarchy);
    console.log('  auditFormData.data?.hierarchy:', auditFormData?.data?.hierarchy);
    console.log('  auditFormData.data?.form_structure:', auditFormData?.data?.form_structure);
    console.log('  cumulativeAuditData keys:', Object.keys(cumulativeAuditData || {}));
    console.log('  cumulativeAuditData sample:', Object.entries(cumulativeAuditData || {}).slice(0, 1));

    // Try to find hierarchy from multiple sources
    let hierarchyData = null;
    if (auditFormData?.hierarchy) {
      hierarchyData = auditFormData.hierarchy;
    } else if (auditFormData?.data?.hierarchy) {
      hierarchyData = auditFormData.data.hierarchy;
    } else if (auditFormData?.data?.form_structure) {
      // Build hierarchy from form structure
      hierarchyData = {
        categories: auditFormData.data.form_structure.map(item => item.categoryData).filter(Boolean),
        control_items: [],
        checks: []
      };
      // Extract control items and checks from form structure
      auditFormData.data.form_structure.forEach(item => {
        if (item.categoryData?.control_items) {
          hierarchyData.control_items.push(...item.categoryData.control_items);
          item.categoryData.control_items.forEach(ci => {
            if (ci.checks) {
              hierarchyData.checks.push(...ci.checks);
            }
          });
        }
      });
    }

    if (!hierarchyData) {
      console.warn('⚠️ No hierarchy data available in any location, returning raw cumulativeAuditData');
      return cumulativeAuditData;
    }

    console.log('  Using hierarchy:', {
      categories: hierarchyData.categories?.length,
      control_items: hierarchyData.control_items?.length,
      checks: hierarchyData.checks?.length
    });

    const enriched = {};

    Object.entries(cumulativeAuditData || {}).forEach(([categoryId, category]) => {
      console.log(`  Processing category ${categoryId}:`, category);

      // Find category in hierarchy
      const categoryInfo = hierarchyData.categories?.find(
        cat => cat.category_id === parseInt(categoryId)
      );

      console.log(`    Found categoryInfo:`, categoryInfo);

      enriched[categoryId] = {
        ...category,
        categoryName: category.categoryName || categoryInfo?.category_name || categoryInfo?.category_code || `Category ${categoryId}`,
        categoryTranslation: category.categoryTranslation || categoryInfo?.category_name_it || categoryInfo?.category_code_it || categoryInfo?.category_name || categoryInfo?.category_code,
        originalCategory: category.originalCategory || { categoryData: categoryInfo }
      };

      // Enrich control items
      if (category.controlItems) {
        enriched[categoryId].controlItems = {};
        Object.entries(category.controlItems).forEach(([controlItemId, controlItem]) => {
          // Find control item in hierarchy
          const controlItemInfo = hierarchyData.control_items?.find(
            item => item.control_item_id === parseInt(controlItemId)
          );

          enriched[categoryId].controlItems[controlItemId] = {
            ...controlItem,
            controlItemName: controlItem.controlItemName || controlItemInfo?.control_item_code || `Control Item ${controlItemId}`,
            controlItemTranslation: controlItem.controlItemTranslation || controlItemInfo?.control_item_code_it || controlItemInfo?.control_item_code,
            originalControlItem: controlItem.originalControlItem || controlItemInfo
          };

          // Enrich checks with names
          if (controlItem.checks) {
            enriched[categoryId].controlItems[controlItemId].checks = {};
            Object.entries(controlItem.checks).forEach(([checkId, check]) => {
              const checkInfo = hierarchyData.checks?.find(
                c => c.check_id === parseInt(checkId) || c.check_id === checkId
              );

              enriched[categoryId].controlItems[controlItemId].checks[checkId] = {
                ...check,
                checkCode: check.checkCode || check.label || checkInfo?.check_code,
                checkCodeIt: check.checkCodeIt || check.labelItalian || checkInfo?.check_code_it,
                label: check.label || checkInfo?.check_code,
                labelItalian: check.labelItalian || checkInfo?.check_code_it,
                originalCheck: check.originalCheck || checkInfo
              };
            });
          }
        });
      }
    });

    return enriched;
  }, [cumulativeAuditData, auditFormData]);

  // Calculate overall statistics
  const calculateStatistics = useCallback(() => {
    let totalCategories = 0;
    let completedCategories = 0;
    let ongoingCategories = 0;
    let notStartedCategories = 0;
    let totalControlItems = 0;
    let completedControlItems = 0;
    let problemControlItems = 0; // NEW: Count control items with problems
    let totalChecks = 0;
    let selectedChecks = 0;
    let totalImages = 0;
    let incompleteItems = [];

    Object.entries(enrichedCumulativeData || {}).forEach(([categoryId, category]) => {
      totalCategories++;

      const controlItems = Object.values(category.controlItems || {});
      totalControlItems += controlItems.length;

      let categoryComplete = true;
      let categoryStarted = false; // Track if any control item in category has been started
      let categoryCompletedItemsCount = 0;
      
      controlItems.forEach(controlItem => {
        const checks = Object.values(controlItem.checks || {});
        totalChecks += checks.length;

        const checkedItems = checks.filter(check => check.isChecked);
        selectedChecks += checkedItems.length;

        // Count control items with ACTUAL problems (deduction points > 0)
        const hasProblems = checkedItems.some(check => {
          const deductionPoints = check.deductionPoints ?? check.deduction_points ??
            check.originalCheck?.deduction_points ?? 0;
          return deductionPoints > 0;
        });

        if (hasProblems) {
          problemControlItems++;
        }
        
        // Count images
        checks.forEach(check => {
          if (check.images) totalImages += check.images.length;
        });
        
        // Check if control item has been started (has any checked items)
        const isControlItemStarted = checkedItems.length > 0;
        if (isControlItemStarted) {
          categoryStarted = true;
        }

        // Check if control item is complete - handle different field name formats
        const isControlItemComplete = checkedItems.length > 0 && checkedItems.every(check => {
          // Handle both deductionPoints and deduction_points
          const deductionPoints = check.deductionPoints ?? check.deduction_points ??
            check.originalCheck?.deduction_points ?? 0;

          // Group A: No problems detected (0 points) - just needs to be checked
          if (deductionPoints === 0) return true;

          // Group B: Problems detected (>0 points) - needs images + comment
          const hasImages = (check.images?.length > 0) ||
                           (check.image_urls?.length > 0) ||
                           (check.attachments?.length > 0);
          const hasComment = (check.comment?.trim()) ||
                            (check.remark?.trim()) ||
                            (check.notes?.trim());

          console.log('🔍 VALIDATION DEBUG:', {
            checkLabel: check.label,
            deductionPoints,
            hasImages,
            hasComment,
            isComplete: hasImages && hasComment,
            checkData: check
          });

          return hasImages && hasComment;
        });

        if (isControlItemComplete) {
          completedControlItems++;
          categoryCompletedItemsCount++;
        } else {
          categoryComplete = false;
          incompleteItems.push({
            category: category.categoryTranslation || category.categoryName,
            controlItem: controlItem.controlItemTranslation || controlItem.controlItemName,
            issues: checkedItems.filter(check => {
              const deductionPoints = check.deductionPoints ?? check.deduction_points ??
                check.originalCheck?.deduction_points ?? 0;
              if (deductionPoints === 0) return false;

              const hasImages = (check.images?.length > 0) ||
                               (check.image_urls?.length > 0) ||
                               (check.attachments?.length > 0);
              const hasComment = (check.comment?.trim()) ||
                                (check.remark?.trim()) ||
                                (check.notes?.trim());

              return !hasImages || !hasComment;
            }).map(check => check.label)
          });
        }
      });

      // Categorize category status
      if (controlItems.length > 0) {
        if (categoryComplete) {
          // All control items in category are complete
          completedCategories++;
        } else if (categoryStarted) {
          // At least one control item started but not all complete
          ongoingCategories++;
        } else {
          // No control items started in this category
          notStartedCategories++;
        }
      }
    });

    return {
      totalCategories,
      completedCategories,
      ongoingCategories,
      notStartedCategories,
      totalControlItems,
      completedControlItems,
      problemControlItems,
      totalChecks,
      selectedChecks,
      totalImages,
      incompleteItems,
      isFullyComplete: incompleteItems.length === 0 && totalControlItems > 0
    };
  }, [enrichedCumulativeData]);

  const stats = calculateStatistics();

  // Calculate deducted points per category
  const calculateCategoryDeductions = useCallback(() => {
    const categoryDeductions = [];
    let totalDeductedPoints = 0;

    Object.entries(enrichedCumulativeData || {}).forEach(([categoryId, category]) => {
      let categoryDeductedPoints = 0;

      Object.values(category.controlItems || {}).forEach(controlItem => {
        Object.values(controlItem.checks || {}).forEach(check => {
          if (check.isChecked) {
            const deductionPoints = check.deductionPoints ?? check.deduction_points ??
              check.originalCheck?.deduction_points ?? 0;
            categoryDeductedPoints += deductionPoints;
          }
        });
      });

      totalDeductedPoints += categoryDeductedPoints;

      categoryDeductions.push({
        categoryId,
        categoryName: language === 'it' && category.categoryTranslation
          ? category.categoryTranslation
          : (category.categoryName || `Category ${categoryId}`),
        deductedPoints: categoryDeductedPoints
      });
    });

    return {
      categoryDeductions: categoryDeductions.sort((a, b) => b.deductedPoints - a.deductedPoints),
      totalDeductedPoints
    };
  }, [enrichedCumulativeData, language]);

  const { categoryDeductions, totalDeductedPoints } = calculateCategoryDeductions();

  // Handle toggle of "Fixed Before Shipment" flag
  const handleToggleFixed = useCallback(async (categoryId, controlItemId, checkId, isFixed) => {

    if (!onUpdateCheckFixedStatus) {
      console.error('🔧 onUpdateCheckFixedStatus callback not provided');
      toast.error('Update callback not available');
      return;
    }

    try {
      // Call parent callback to update the state
      const result = await onUpdateCheckFixedStatus(categoryId, controlItemId, checkId, isFixed);

      if (result && result.success) {
        toast.success(isFixed ? 'Marked as fixed before shipment' : 'Unmarked as fixed');
      } else {
        toast.error('Failed to update: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to toggle fixed status:', error);
      toast.error('Failed to update fixed status');
    }
  }, [onUpdateCheckFixedStatus, toast]);

  // Validate audit before submission
  const validateAuditForSubmission = useCallback(() => {
    const results = {
      isValid: true,
      errors: [],
      warnings: []
    };

    if (stats.totalControlItems === 0) {
      results.isValid = false;
      results.errors.push(t('summary.no_control_items_to_audit'));
    }

    if (stats.selectedChecks === 0) {
      results.isValid = false;
      results.errors.push(t('summary.no_check_items_selected'));
    }

    if (stats.incompleteItems.length > 0) {
      results.isValid = false;
      results.errors.push(`${stats.incompleteItems.length} control items are incomplete`);
    }

    if (!currentScore || currentScore.percentage === 0) {
      results.warnings.push(t('summary.score_calculation_incomplete'));
    }

    return results;
  }, [stats, currentScore]);

  // Handle final submission
  const handleFinalSubmit = async () => {
    const validation = validateAuditForSubmission();
    setValidationResults(validation);

    if (!validation.isValid) {
      toast.error(t('summary.cannot_submit_with_errors'));
      return;
    }

    if (validation.warnings.length > 0) {
      toast.warning(t('summary.warnings_review_before_submit'));
    }

    setSubmitDialogOpen(true);
  };

  const confirmSubmission = async () => {
    setSubmitDialogOpen(false);
    try {
      await onFinalSubmit();
      toast.success(t('summary.audit_submitted_success'));
    } catch (error) {
      toast.error(t('summary.failed_to_submit'));
      console.error("Submission error:", error);
    }
  };

  // Verify data structure
  console.log('✅ AUDIT SUMMARY PAGE:', {
    categoriesFound: cumulativeAuditData ? Object.keys(cumulativeAuditData).length : 0,
    hasControlItemData: cumulativeAuditData && Object.values(cumulativeAuditData).some(cat =>
      cat.controlItems && Object.keys(cat.controlItems).length > 0
    )
  });

  // Debug serial number data
  console.log('🔍 SERIAL NUMBER DEBUG:', {
    hasCurrentAuditResponse: !!currentAuditResponse,
    unit_serial_number: currentAuditResponse?.unit_serial_number,
    serial_number: currentAuditResponse?.serial_number,
    chiller_serial_number: currentAuditResponse?.chiller_serial_number,
    raw_response_json_serialNumber: currentAuditResponse?.raw_response_json?.serialNumber,
    raw_response_json_unitDetailsData: currentAuditResponse?.raw_response_json?.unitDetailsData,
    raw_response_json_keys: currentAuditResponse?.raw_response_json ? Object.keys(currentAuditResponse.raw_response_json) : [],
    selectedForm_serial_number: selectedForm?.serial_number,
    allKeys: currentAuditResponse ? Object.keys(currentAuditResponse) : []
  });

  if (!cumulativeAuditData || Object.keys(cumulativeAuditData).length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">{t('summary.no_audit_data')}</h2>
          <p className="text-gray-500 mb-4">{t('summary.complete_audit_items')}</p>
          <div className="text-xs text-gray-400 mb-4 bg-gray-100 p-2 rounded">
            Debug: {Object.keys(cumulativeAuditData || {}).length} categories found
          </div>
          <Button onClick={onNavigateBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go {t('summary.back_to_audit')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('summary.title')}</h1>
              <p className="text-gray-600">
                {t('summary.review_before_submit')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <LanguageToggle variant="button" />
              {onNavigateToDashboard && (
                <Button variant="outline" onClick={onNavigateToDashboard}>
                  {t('btn.dashboard')}
                </Button>
              )}
              <Button variant="outline" onClick={onNavigateBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('summary.back_to_audit')}
              </Button>
              <Button
                onClick={handleFinalSubmit}
                disabled={isSubmitting || !stats.isFullyComplete}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('summary.submitting')}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t('summary.submit_audit')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Summary Cards */}
          <div className="lg:col-span-1 space-y-4">
            {/* Audit Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {t('summary.audit_information')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">
                    <strong>{t('summary.id')}</strong> AUD-{currentAuditResponse?.audit_form_response_id || 'NEW'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">
                    <strong>{t('summary.created')}</strong> {currentAuditResponse?.created_at
                      ? new Date(currentAuditResponse.created_at).toLocaleDateString()
                      : t('summary.today')
                    }
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">
                    <strong>{t('summary.serial')}</strong> {currentAuditResponse?.chiller_serial_number || currentAuditResponse?.serial_number || currentAuditResponse?.raw_response_json?.unitDetailsData?.serialNumber || currentAuditResponse?.raw_response_json?.serialNumber || selectedForm?.serial_number || t('summary.na')}
                  </span>
                </div>
              </CardContent>
            </Card>


            {/* Statistics Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('summary.progress_overview')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Categories Section */}
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-gray-700">{t('summary.categories')}:</div>
                  <div className="pl-3 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-green-600">✓</span>
                      <span className="text-gray-600">{t('summary.completed')}:</span>
                      <span className="font-medium">{stats.completedCategories}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-yellow-600">⏳</span>
                      <span className="text-gray-600">{t('summary.ongoing')}:</span>
                      <span className="font-medium">{stats.ongoingCategories}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400">⭕</span>
                      <span className="text-gray-600">{t('summary.not_started')}:</span>
                      <span className="font-medium">{stats.notStartedCategories}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm pt-1 border-t border-gray-200">
                      <span className="text-gray-600 font-semibold">{t('summary.total')}:</span>
                      <span className="font-bold">{stats.totalCategories}</span>
                    </div>
                  </div>
                </div>

                {/* Control Items Section */}
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">{t('summary.control_items')}:</span>
                  <span className="text-sm font-medium">{stats.completedControlItems} / {stats.totalControlItems}</span>
                </div>
              </CardContent>
            </Card>

            {/* Category Deductions Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{t('summary.category_deductions')}</span>
                  <Badge variant="destructive" className="text-base">
                    {totalDeductedPoints} pts
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {categoryDeductions.length > 0 ? (
                  <div className="space-y-2">
                    {categoryDeductions.map((category) => (
                      <div
                        key={category.categoryId}
                        className="flex justify-between items-center p-2 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-sm font-medium text-gray-700">
                          {category.categoryName}
                        </span>
                        <Badge
                          variant={category.deductedPoints > 0 ? "destructive" : "secondary"}
                          className="ml-2"
                        >
                          {category.deductedPoints > 0 ? `-${category.deductedPoints}` : '0'} pts
                        </Badge>
                      </div>
                    ))}
                    <div className="border-t pt-2 mt-3 space-y-2">
                      <div className="flex justify-between items-center font-semibold">
                        <span className="text-sm text-gray-900">{t('summary.total_deductions')}</span>
                        <Badge variant="destructive" className="text-base">
                          -{totalDeductedPoints} pts
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center font-bold">
                        <span className="text-sm text-gray-900">{t('summary.total_score')}</span>
                        <Badge
                          variant={currentScore?.percentage >= 100 ? "default" : "secondary"}
                          className="text-lg"
                        >
                          {currentScore?.achieved || 0} pts
                        </Badge>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No deductions recorded
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Validation Results */}
            {validationResults && (
              <Card className={validationResults.isValid ? "border-green-200" : "border-red-200"}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {validationResults.isValid ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600" />
                    )}
                    {t('summary.validation_status')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {validationResults.errors.length > 0 && (
                    <div className="space-y-1 mb-3">
                      <p className="text-sm font-medium text-red-600">{t('summary.errors')}</p>
                      {validationResults.errors.map((error, index) => (
                        <p key={index} className="text-sm text-red-600">• {error}</p>
                      ))}
                    </div>
                  )}
                  {validationResults.warnings.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-yellow-600">{t('summary.warnings')}</p>
                      {validationResults.warnings.map((warning, index) => (
                        <p key={index} className="text-sm text-yellow-600">• {warning}</p>
                      ))}
                    </div>
                  )}
                  {validationResults.isValid && validationResults.errors.length === 0 && (
                    <p className="text-sm text-green-600">{t('summary.ready_for_submission')}</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Detailed Review */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('summary.detailed_review')}</CardTitle>
                <p className="text-sm text-gray-600">
                  {t('summary.review_categories_items')}
                </p>
              </CardHeader>
              <CardContent>
                {/* Tab Navigation */}
                <div className="flex gap-2 mb-4 border-b">
                  <button
                    onClick={() => setActiveTab('problems')}
                    className={cn(
                      "px-4 py-2 font-medium border-b-2 transition-colors flex items-center gap-2",
                      activeTab === 'problems'
                        ? "border-red-500 text-red-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    )}
                  >
                    <AlertCircle className="h-4 w-4" />
                    <span>{t('summary.problems_only')}</span>
                    {stats.problemControlItems > 0 && (
                      <Badge variant="destructive" className="ml-1">
                        {stats.problemControlItems}
                      </Badge>
                    )}
                  </button>

                  <button
                    onClick={() => setActiveTab('all')}
                    className={cn(
                      "px-4 py-2 font-medium border-b-2 transition-colors flex items-center gap-2",
                      activeTab === 'all'
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    )}
                  >
                    <FileText className="h-4 w-4" />
                    <span>{t('summary.all_items')}</span>
                    <Badge variant="secondary" className="ml-1">
                      {stats.totalControlItems}
                    </Badge>
                  </button>
                </div>

                {/* Info Banner for Problems Only Tab */}
                {activeTab === 'problems' && stats.problemControlItems > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      ℹ️ {t('summary.showing_problems_count').replace('{0}', stats.problemControlItems)}
                      {stats.totalControlItems - stats.problemControlItems > 0 && (
                        <span className="ml-1">
                          ({stats.totalControlItems - stats.problemControlItems} {t('summary.items_with_no_problems')})
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {/* Empty State - No Problems Detected */}
                {activeTab === 'problems' && stats.problemControlItems === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-green-700 mb-2">
                      {t('summary.no_problems_detected')}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {t('summary.all_control_items_passed')}
                    </p>
                    <Button
                      onClick={() => setActiveTab('all')}
                      variant="outline"
                      className="mt-2"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      {t('summary.view_all_items')}
                    </Button>
                  </div>
                ) : (
                  <ScrollArea className="h-[600px] pr-4">
                  {Object.entries(enrichedCumulativeData).map(([categoryId, category]) => {
                    // Debug logging for category names
                    console.log(`📋 Rendering category ${categoryId}:`, {
                      categoryName: category.categoryName,
                      categoryTranslation: category.categoryTranslation,
                      hasOriginalCategory: !!category.originalCategory,
                      language,
                      activeTab
                    });

                    return (
                    <div key={categoryId} className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold">
                            {language === 'it' && category.categoryTranslation
                              ? category.categoryTranslation
                              : (category.categoryName || `Category ${categoryId}`)}
                          </h3>
                          {category.categoryTranslation && category.categoryTranslation !== category.categoryName && (
                            <p className="text-sm text-gray-600">
                              {language === 'it' ? category.categoryName : category.categoryTranslation}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEditCategory(category)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          {t('summary.edit_category')}
                        </Button>
                      </div>
                      
                      {Object.entries(category.controlItems || {})
                        .filter(([controlItemId, controlItem]) => {
                          // If "All Items" tab, show everything
                          if (activeTab === 'all') return true;

                          // If "Problems Only" tab, show only items with ACTUAL problems (deduction points > 0)
                          const checks = Object.values(controlItem.checks || {});
                          return checks.some(check => {
                            if (!check.isChecked) return false;

                            const deductionPoints = check.deductionPoints ?? check.deduction_points ??
                              check.originalCheck?.deduction_points ?? 0;

                            return deductionPoints > 0; // Only count as problem if deduction points > 0
                          });
                        })
                        .map(([controlItemId, controlItem]) => (
                          <ControlItemSummary
                            key={controlItemId}
                            controlItem={controlItem}
                            controlItemId={controlItemId}
                            categoryId={categoryId}
                            categoryName={category.categoryName || `Category ${categoryId}`}
                            category={category}
                            onEdit={onEditControlItem}
                            onToggleFixed={handleToggleFixed}
                          />
                        ))
                      }
                      
                      {/* Show if no control items */}
                      {(!category.controlItems || Object.keys(category.controlItems).length === 0) && (
                        <div className="text-gray-500 text-sm italic">
                          {t('summary.no_control_items_found')}
                        </div>
                      )}
                    </div>
                  );
                  })}
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Final Submission Confirmation Dialog */}
      <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              ⚠️ {t('summary.final_audit_submission')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <p className="text-sm text-yellow-800 font-medium mb-2">
                🚨 {t('summary.irreversible_action')}
              </p>
              <p className="text-sm text-yellow-700">
                {t('summary.cannot_make_changes')}
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <p className="text-sm font-medium text-blue-800 mb-2">{t('summary.audit_summary_stats')}</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>✅ {stats.completedControlItems} {t('summary.control_items_completed')}</li>
                <li>✅ {stats.selectedChecks} {t('summary.check_items_selected')}</li>
                <li>✅ {stats.totalImages} {t('summary.images_uploaded_count')}</li>
                <li>✅ {t('summary.all_validations_passed')}</li>
              </ul>
            </div>

            <div className="text-center text-sm text-gray-600">
              <p>{t('summary.need_changes')}</p>
              <p>{t('summary.ready_finalize')}</p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => setSubmitDialogOpen(false)}
                className="flex-1"
              >
                {t('btn.cancel')}
              </Button>
              {onNavigateToDashboard && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSubmitDialogOpen(false);
                    onNavigateToDashboard();
                  }}
                  className="flex-1"
                >
                  {t('btn.dashboard')}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setSubmitDialogOpen(false);
                  onNavigateBack();
                }}
                className="flex-1"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('summary.back_to_audit')}
              </Button>
              <Button
                onClick={confirmSubmission}
                disabled={isSubmitting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('summary.submitting')}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t('summary.submit_audit')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditSummaryPage;